/**
 * One request's git history (git sync Stage 1, card 9): the syncs that
 * changed it, newest first, following renames and moves.
 *
 * A request's file is named after the request, so renaming it — or moving it
 * to another collection — changes the path. Like `git log --follow`, a sync
 * that ADDS the file is checked for a request file it DELETED with the same
 * `id`; if there is one, the history continues at that older path. A version
 * only counts when the file at that sync carries this request's id, so a name
 * reused by another request never mixes its history in.
 */
import { invoke } from '@tauri-apps/api/core';
import { mirrorAbsDir } from '../workspaceMirror';
import { refreshMirror } from '../workspaceGitSync';
import { readTextSafe } from '../gitFileWriter.js';
import { parseYaml } from '../../utils/yamlWorkspace/deterministicYaml.js';
import { groupOfPath } from './changeItems';

const MAX_HOPS = 20; // renames followed
const LIMIT = 200; // syncs read per path

function parsed(text) {
  if (text == null) return null;
  try {
    const v = parseYaml(text);
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
}

/** The `id` in a request file's text, as a string; null when there is none. */
export function idOf(text) {
  const v = parsed(text);
  return v && v.id != null ? String(v.id) : null;
}

const nameOf = (text) => parsed(text)?.name || null;

const isRequestFile = (p) =>
  p.endsWith('.yaml') && !p.endsWith('.local.yaml') && groupOfPath(p) === 'Requests';

/** Every request file in the mirror folder, repo-relative (no .git, no dotfiles). */
async function listRequestFiles(fs, dir, rel = '') {
  const out = [];
  let entries = [];
  try {
    entries = await fs.readDir(rel ? `${dir}/${rel}` : dir);
  } catch {
    return out;
  }
  for (const e of entries) {
    if (!e?.name || e.name.startsWith('.')) continue;
    const p = rel ? `${rel}/${e.name}` : e.name;
    if (e.isDirectory) out.push(...(await listRequestFiles(fs, dir, p)));
    else if (isRequestFile(p)) out.push(p);
  }
  return out;
}

/** The mirror file that holds request `id` today, or null. */
export async function findRequestPath(fs, dir, id) {
  for (const p of await listRequestFiles(fs, dir)) {
    if (idOf(await readTextSafe(fs, `${dir}/${p}`)) === String(id)) return p;
  }
  return null;
}

/**
 * The versions of item `id`, newest first, from `startPath` back through renames.
 * `history(path)` = git_history for that path (newest first, every file of each
 * sync); `fileAt(oid, path)` = the file's text at a sync (null when absent).
 *
 * @returns {Promise<Array<{oid, parent, time, author, message, path, beforePath, kind, after}>>}
 *   path = the file at that sync; beforePath = the file at its parent (differs on a rename);
 *   after = the text the sync left (null when it deleted the file).
 */
/** How a request file says whose it is (the default kind for followItem). */
export const REQUEST_ITEMS = Object.freeze({ idOf, nameOf: (text) => nameOf(text), isItemFile: (p) => isRequestFile(p) });

export async function followItem({ startPath, id, history, fileAt, items = REQUEST_ITEMS }) {
  const want = String(id);
  const versions = [];
  let path = startPath;
  let olderThan = null; // after a rename: only syncs older than the rename
  for (let hop = 0; hop < MAX_HOPS && path; hop += 1) {
    const at = path;
    const stopAt = olderThan;
    let commits = (await history(at)) || [];
    if (stopAt) {
      const i = commits.findIndex((c) => c.oid === stopAt);
      commits = i >= 0 ? commits.slice(i + 1) : [];
    }
    let next = null;
    for (const c of commits) {
      const f = (c.files || []).find((x) => x.path === at);
      const kind = f?.kind || 'modified';
      const after = kind === 'deleted' ? null : await fileAt(c.oid, at);
      const mine = kind === 'deleted' ? items.idOf(await fileAt(c.parent, at)) === want : items.idOf(after) === want;
      if (!mine) continue;
      const v = { oid: c.oid, parent: c.parent, time: c.time, author: c.author, message: c.message, path: at, beforePath: at, kind, after };
      versions.push(v);
      if (kind === 'added' && c.parent) {
        const gone = (c.files || []).filter((x) => x.kind === 'deleted' && x.path !== at && items.isItemFile(x.path));
        for (const d of gone) {
          const was = await fileAt(c.parent, d.path);
          if (items.idOf(was) === want) {
            v.beforePath = d.path;
            v.kind = 'modified';
            v.renamedFrom = d.path;
            v.oldName = items.nameOf(was);
            v.newName = items.nameOf(after);
            next = d.path;
            break;
          }
        }
        if (next) {
          olderThan = c.oid;
          break;
        }
      }
    }
    path = next;
  }
  return versions;
}

/**
 * Open a request's history: bring the mirror up to date with the app (the
 * same refresh Changes and Restore do), find the request's file by its id,
 * and follow it back.
 *
 * @returns {Promise<{ path: string|null, unsynced: boolean, versions: Array }>}
 */
export async function loadItemHistory(workspace, snapshot, id) {
  if (!workspace?.git || id == null) return { path: null, unsynced: false, versions: [] };
  await refreshMirror(workspace, snapshot);
  const dir = (await mirrorAbsDir(workspace.id)).replace(/\\/g, '/');
  const fs = await import('@tauri-apps/plugin-fs');
  const path = await findRequestPath(fs, dir, id);
  if (!path) return { path: null, unsynced: false, versions: [] };
  const fileAt = (oid, p) => (oid ? invoke('git_file_at', { repoPath: dir, oid, path: p }) : Promise.resolve(null));
  const versions = await followItem({
    startPath: path,
    id,
    history: (p) => invoke('git_history', { repoPath: dir, path: p, limit: LIMIT }),
    fileAt,
  });
  const now = await readTextSafe(fs, `${dir}/${path}`);
  const head = await invoke('git_file_at_head', { repoPath: dir, path }).catch(() => null);
  return { path, unsynced: now != null && now !== head, versions, fileAt };
}
